<script setup>
import { ref, watch } from 'vue';
import ModalWrapper from './ModalWrapper.vue';
import { rpc } from '../../../composables/useRpc.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: String, required: true },
  quotations: { type: Array, default: () => [] },
  preselectedId: { type: String, default: null },
});
const emit = defineEmits(['close', 'sent']);

const selectedId = ref(props.preselectedId || null);
const mode = ref('prices');
const sending = ref(false);
const error = ref(null);

// Sync preselectedId when modal opens
watch(() => props.show, (val) => {
  if (val) {
    selectedId.value = props.preselectedId || null;
    mode.value = 'prices';
    error.value = null;
  }
});

const MODES = [
  { value: 'prices',      label: 'Solo precios' },
  { value: 'link',        label: 'Solo link' },
  { value: 'prices_link', label: 'Precios + link' },
  { value: 'pdf',         label: 'PDF' },
];

async function handleSend() {
  if (!selectedId.value || sending.value) return;
  sending.value = true;
  error.value = null;
  try {
    await rpc('send-quotation', {
      body: {
        quotation_id: selectedId.value,
        conversation_id: props.conversationId,
        mode: mode.value,
      },
    });
    emit('sent');
  } catch (err) {
    console.error('[SendQuotationModal]', err);
    error.value = 'Error al enviar. Intentá de nuevo.';
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <ModalWrapper :show="show" title="Enviar cotización" @close="emit('close')">
    <!-- Quotation list -->
    <div class="sqm-list">
      <label
        v-for="q in quotations"
        :key="q.id"
        class="sqm-item"
        :class="{ 'sqm-item--active': q.id === selectedId }"
      >
        <input
          type="radio"
          class="sqm-radio"
          :value="q.id"
          v-model="selectedId"
        />
        <div class="sqm-info">
          <span class="sqm-name">#{{ q.number }} — {{ q.title }}</span>
          <span class="sqm-meta">{{ q.status }} · ${{ q.total?.toLocaleString('es-AR') ?? '—' }}</span>
        </div>
      </label>

      <div v-if="quotations.length === 0" class="sqm-empty">
        Sin cotizaciones para este cliente
      </div>
    </div>

    <!-- Send mode + action — always visible -->
    <div class="sqm-form">
      <p class="sqm-form-label">Formato de envío</p>
      <div class="sqm-modes">
        <label
          v-for="m in MODES"
          :key="m.value"
          class="sqm-mode"
          :class="{ 'sqm-mode--active': mode === m.value }"
        >
          <input type="radio" :value="m.value" v-model="mode" class="sqm-radio" />
          {{ m.label }}
        </label>
      </div>

      <p v-if="error" class="sqm-error">{{ error }}</p>

      <button
        class="sqm-send-btn"
        :disabled="!selectedId || sending"
        @click="handleSend"
      >
        {{ sending ? 'Enviando…' : 'Enviar' }}
      </button>
    </div>
  </ModalWrapper>
</template>

<style scoped>
/* Quotation list */
.sqm-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
  max-height: 240px;
  overflow-y: auto;
}

.sqm-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgb(var(--slate-4, 232 232 236));
  background: rgb(var(--slate-1, 252 252 253));
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.sqm-item:hover {
  background: rgb(var(--slate-2, 246 246 249));
}

.sqm-item--active {
  border-color: rgb(var(--violet-8, 110 86 207));
  background: rgb(var(--violet-2, 241 238 255));
}

.sqm-radio {
  accent-color: rgb(var(--violet-9, 96 72 194));
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.sqm-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.sqm-name {
  font-size: 13px;
  font-weight: 500;
  color: rgb(var(--slate-12, 28 32 36));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sqm-meta {
  font-size: 11px;
  color: rgb(var(--slate-10, 140 144 152));
}

.sqm-empty {
  font-size: 13px;
  color: rgb(var(--slate-10, 140 144 152));
  text-align: center;
  padding: 20px 0;
}

/* Send form */
.sqm-form {
  border-top: 1px solid rgb(var(--slate-4, 232 232 236));
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sqm-form-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgb(var(--slate-10, 140 144 152));
  margin: 0;
}

.sqm-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.sqm-mode {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 44px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid rgb(var(--slate-4, 232 232 236));
  background: rgb(var(--slate-1, 252 252 253));
  font-size: 13px;
  color: rgb(var(--slate-11, 96 100 108));
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
}

.sqm-mode:hover {
  background: rgb(var(--slate-2, 246 246 249));
}

.sqm-mode--active {
  border-color: rgb(var(--violet-8, 110 86 207));
  background: rgb(var(--violet-2, 241 238 255));
  color: rgb(var(--violet-11, 70 50 168));
  font-weight: 500;
}

.sqm-error {
  font-size: 12px;
  color: rgb(var(--ruby-9, 229 70 102));
  margin: 0;
}

.sqm-send-btn {
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  border: none;
  background: rgb(var(--violet-9, 96 72 194));
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
}

.sqm-send-btn:hover:not(:disabled) {
  background: rgb(var(--violet-10, 80 58 180));
}

.sqm-send-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
